import * as eventsDimensions from './events.dimensions';
import pushToDataLayer from 'utils/gtm/api/pushToDataLayer';
import { log, LOG_GROUPS } from 'utils/log';

/**
 * Logs out successful event pushes.
 * @param {string} method The method that was called to generate the payload.
 * @param {Object} eventPayload The entire payload that got sent to GTM.
 * @param {string} eventInfo.event The name of the event that got pushed.
 * @param {Object} eventInfo.eventData The object that got pushed.
 * @returns {void}
 */
const eventSuccess = (method, { event, eventData }) => {
  log(
    LOG_GROUPS.GTM,
    `Event '${event}' successfully pushed to GTM (via method '${method}'), along with the following payload:`
  );
  log(LOG_GROUPS.GTM, { eventData });
};

/**
 * Logs error messages to the console.
 * @param {string} msg The error message.
 * @returns {void}
 */
const eventFailure = msg => {
  console.error(msg);
};

/**
 * Pushes a namespaced event to GTM, generating and normalising the payload.
 * @param {Object} config The event configuration.
 * @param {string} config.method The payload generation method to call inside events.dimensions.js.
 * @param {Object} config.args The arguments to pass down to the called method.
 * @param {Object} config.eventName The event name configuration.
 * @param {string[]} config.eventName.namspace The event namespace nesting.
 * @param {Object} config.eventName.action The event action.
 * @returns {void}
 */
export const gtmEventPush = ({
  method,
  args = {},
  eventName: { namespace = [], action = '' } = {}
}) => {
  const eventsDimension = eventsDimensions[method];

  // Log an error if the method doesn't exist.
  if (!eventsDimension) {
    eventFailure(`${method} is not defined in events.dimensions.`);
  } else if ((namespace.length && !action) || (action && !namespace.length)) {
    // If an event name has been passed in, it must contain both an action and a namespace.
    eventFailure(
      'When passing in an event name, you must provide both a namespace and action property.'
    );
  } else {
    // Call the correct function to generate the payload.
    let eventData;

    try {
      eventData = eventsDimension(args);
    } catch (e) {
      console.error(e);
    }

    // Construct the event suffix - based off the name and action if passed in, or the method if not.
    const namespaceArray = [...namespace, action];
    const namespaceDotPath = namespaceArray.join('.');
    const eventSuffix = namespaceDotPath || method;

    // Transform the payload that gets sent to GTM to follow our convention.
    const payload = {
      event: `cf.${eventSuffix}`,
      eventData
    };

    // Push to the data layer and log a success message.
    pushToDataLayer(payload);
    eventSuccess(method, payload);
  }
};



// WEBPACK FOOTER //
// ./src/utils/gtm/events/index.js